import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormGroup, Input, Label, Spinner } from "reactstrap";
import Select from "react-select";

import { previaBid } from "services/bidFreteApi";
import { makeReactSelectStyles, reactSelectPortalProps } from "utils/reactSelectStyles";
import { PERIODO_MAX_DIAS, REGIAO_BRASIL, REGIOES, erroDaApi, fmtNumero, fmtPct, montarEscopoPrevia, periodoValido, resumoPrevia, ufsDoEscopo, validarEscopo } from "../regras";

// Dentro de modal: o menu vai para o body (portal) e precisa ficar acima do
// .modal (z-index 1050), senão abre por trás e só dá para digitar.
const selectNoModalStyles = makeReactSelectStyles({ portalZIndex: 1070 });

// Passo 2 do NovoBidWizard (H2 PO1-2841): escopo com período (teto de 90 dias),
// UFs/regiões de destino, transportadora atual, negócio, serviço e modal
// (react-select isMulti no molde de FaturamentoFiltros.js:393-470) e a prévia
// automática com debounce (POST /gobid/bids/previa só conta).

const DEBOUNCE_MS = 600;

// Brasil e regiões se excluem: escolher Brasil limpa as regiões; escolher uma
// região com Brasil marcado tira o Brasil.
function regioesEscolhidas(antes, depois) {
  const tinhaBrasil = (antes || []).some((o) => o.value === REGIAO_BRASIL);
  const temBrasil = depois.some((o) => o.value === REGIAO_BRASIL);
  if (temBrasil && !tinhaBrasil) return depois.filter((o) => o.value === REGIAO_BRASIL);
  if (temBrasil && depois.length > 1) return depois.filter((o) => o.value !== REGIAO_BRASIL);
  return depois;
}

function Multi({ label, options, value, onChange, placeholder, loading }) {
  return (
    <FormGroup>
      <Label>{label}</Label>
      <Select
        isMulti
        className="react-select info"
        classNamePrefix="react-select"
        options={options}
        value={value}
        onChange={(v) => onChange(v || [])}
        isDisabled={loading}
        styles={selectNoModalStyles}
        placeholder={placeholder || "Todos"}
        noOptionsMessage={() => "Sem opções"}
        {...reactSelectPortalProps()}
      />
    </FormGroup>
  );
}

export default function StepEscopo({ form, onChange, listas, loadingListas, empresaId, erros, previa, onPrevia }) {
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [erroPrevia, setErroPrevia] = useState(null);
  const timerRef = useRef(null);
  const seqRef = useRef(0);

  const opts = useMemo(
    () => ({
      estados: (listas.estados || []).map((x) => ({ value: x.uf || x.sigla || x.id, label: x.uf ? `${x.uf} - ${x.nome}` : x.nome })),
      // Regiões geográficas (UFS_POR_REGIAO da spec), não as RegiaoFrete do embarcador.
      // "Brasil" primeiro: atalho para as 5 regiões (expandido em montarPayload).
      regioes: [{ value: REGIAO_BRASIL, label: "Brasil (todas as regiões)" }].concat(Object.keys(REGIOES).map((sigla) => ({ value: sigla, label: REGIOES[sigla] }))),
      transportadoras: (listas.transportadoras || []).map((x) => ({ value: x.id || x.Id || x.value || x.transportadora_id, label: x.label || x.transportadora_fantasia || x.fantasia || x.nome || x.razaoSocial || String(x.id) })),
      negocios: (listas.negocios || []).map((x) => ({ value: x.id, label: x.descricao || x.nome })),
      servicos: (listas.servicos || []).map((x) => ({ value: x.id || x.nome, label: x.nome })),
      modais: (listas.modais || []).map((x) => ({ value: x.id, label: x.nome })),
    }),
    [listas],
  );

  const periodo = periodoValido(form.dataEmissaoDe, form.dataEmissaoAte);
  const ufsEfetivas = ufsDoEscopo(
    (form.ufs || []).map((o) => o.value),
    (form.regioes || []).map((o) => o.value),
  );
  const escopoOk = Object.keys(validarEscopo(form)).length === 0;

  // Prévia automática com debounce: só com escopo válido.
  const chave = JSON.stringify(montarEscopoPrevia(form));
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!empresaId || !escopoOk) {
      setCarregandoPrevia(false);
      onPrevia(null);
      return undefined;
    }
    const seq = (seqRef.current += 1);
    setCarregandoPrevia(true);
    setErroPrevia(null);
    timerRef.current = setTimeout(() => {
      previaBid(empresaId, JSON.parse(chave))
        .then((r) => {
          if (seq !== seqRef.current) return;
          onPrevia(r);
        })
        .catch((err) => {
          if (seq !== seqRef.current) return;
          const e = erroDaApi(err, "Não foi possível calcular a prévia.");
          setErroPrevia(e.errors ? `${e.mensagem} ${Object.values(e.errors).join(" ")}` : e.mensagem);
          onPrevia(null);
        })
        .finally(() => {
          if (seq === seqRef.current) setCarregandoPrevia(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, empresaId, escopoOk]);

  const p = resumoPrevia(previa);

  return (
    <>
      <div className="lf-section">
        <div className="lf-section-title">Período de emissão (até {PERIODO_MAX_DIAS} dias)</div>
        <div className="bf-grid-2">
          <FormGroup>
            <Label>De</Label>
            <Input
              type="date"
              value={form.dataEmissaoDe || ""}
              onChange={(e) => {
                const v = e.target.value;
                onChange({ dataEmissaoDe: v });
              }}
            />
          </FormGroup>
          <FormGroup>
            <Label>Até</Label>
            <Input
              type="date"
              value={form.dataEmissaoAte || ""}
              onChange={(e) => {
                const v = e.target.value;
                onChange({ dataEmissaoAte: v });
              }}
            />
          </FormGroup>
        </div>
        {erros.periodo ? <div className="lf-erro-campo">{erros.periodo}</div> : periodo.ok ? <div className="lf-muted">{periodo.dias} dia(s) de emissão.</div> : null}
      </div>

      <div className="lf-section">
        <div className="lf-section-title">Destino</div>
        <div className="bf-grid-2">
          <Multi label="Regiões de destino" options={opts.regioes} value={form.regioes || []} onChange={(v) => onChange({ regioes: regioesEscolhidas(form.regioes, v) })} placeholder="Brasil ou regiões inteiras" loading={loadingListas} />
          <Multi label="UFs de destino" options={opts.estados} value={form.ufs || []} onChange={(v) => onChange({ ufs: v })} placeholder="Ou UFs avulsas" loading={loadingListas} />
        </div>
        {erros.destino ? <div className="lf-erro-campo">{erros.destino}</div> : ufsEfetivas.length ? <div className="lf-muted">{ufsEfetivas.length} UF(s): {ufsEfetivas.join(", ")}</div> : null}
      </div>

      <div className="lf-section">
        <div className="lf-section-title">Recorte (opcional)</div>
        <div className="bf-grid-2">
          <Multi label="Transportadora atual" options={opts.transportadoras} value={form.transportadorasAtuais || []} onChange={(v) => onChange({ transportadorasAtuais: v })} loading={loadingListas} />
          <Multi label="Negócio" options={opts.negocios} value={form.negocios || []} onChange={(v) => onChange({ negocios: v })} loading={loadingListas} />
          <Multi label="Serviço" options={opts.servicos} value={form.servicos || []} onChange={(v) => onChange({ servicos: v })} loading={loadingListas} />
          <Multi label="Modal" options={opts.modais} value={form.modais || []} onChange={(v) => onChange({ modais: v })} loading={loadingListas} />
        </div>
      </div>

      <div className="lf-section">
        <div className="lf-section-title">
          Prévia {carregandoPrevia ? <Spinner size="sm" style={{ marginLeft: 6 }} /> : null}
        </div>
        {!escopoOk ? (
          <div className="lf-muted">Informe o período e o destino para ver a prévia.</div>
        ) : erroPrevia ? (
          <div className="lf-aviso lf-aviso-danger">{erroPrevia}</div>
        ) : !p ? (
          <div className="lf-muted">{carregandoPrevia ? "Contando as NFs do escopo..." : "Sem prévia."}</div>
        ) : (
          <>
            <div className="bf-previa">
              <div className="lf-kpi">
                <div className="lf-kpi-label">NFs</div>
                <div className="lf-kpi-value">{fmtNumero(p.qtdNf)}</div>
              </div>
              <div className="lf-kpi">
                <div className="lf-kpi-label">Com CTe</div>
                <div className="lf-kpi-value">{fmtNumero(p.qtdComCte)}</div>
                <div className="lf-kpi-sub">{p.coberturaCte != null ? `${fmtPct(p.coberturaCte)} comparáveis` : "—"}</div>
              </div>
              <div className="lf-kpi">
                <div className="lf-kpi-label">Romaneios de lotação</div>
                <div className="lf-kpi-value">{fmtNumero(p.qtdRomaneiosLotacao)}</div>
              </div>
              <div className="lf-kpi">
                <div className="lf-kpi-label">Candidatas</div>
                <div className="lf-kpi-value">{fmtNumero(p.candidatasTotal)}</div>
                <div className="lf-kpi-sub">
                  {fmtNumero(p.candidatasTabela)} por tabela · {fmtNumero(p.candidatasConector)} por conector
                  {p.candidatasProposta ? ` · ${fmtNumero(p.candidatasProposta)} propostas` : ""}
                </div>
              </div>
              <div className="lf-kpi">
                <div className="lf-kpi-label">Estimativa</div>
                <div className="lf-kpi-value">{fmtNumero(p.estimativaCalculos)}</div>
                <div className="lf-kpi-sub">
                  cálculos{p.estimativaChamadasConector ? ` + ${fmtNumero(p.estimativaChamadasConector)} chamadas de conector` : ""}
                  {p.estimativaMinutos != null ? ` · ~${fmtNumero(p.estimativaMinutos)} min` : ""}
                </div>
              </div>
            </div>
            {p.qtdNf === 0 ? <div className="lf-aviso lf-aviso-warning" style={{ marginTop: 10 }}>Nenhuma NF no escopo. Amplie o período ou o destino.</div> : null}
            {p.qtdNf > 0 && p.coberturaCte !== null && p.coberturaCte < 0.5 ? (
              <div className="lf-aviso lf-aviso-warning" style={{ marginTop: 10 }}>
                Menos da metade das NFs tem CTe: a economia só é medida nas comparáveis.
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}
